import React, { useContext, useId } from 'react'
import styled, { ThemeContext } from 'styled-components'

// Utils
import releaseNoteIllustrations, {
  IllustrationShape,
  IllustrationTone,
} from './releaseNoteIllustrations'

import { ITheme, useAppState } from '../../context/AppContext'

// Types
import { ICompanyCultureValuePure } from '../../common/models/Company'

interface IProps {
  name: string
  className?: string
}

// Accents come from the culture values (every company has at least three), with
// primary shades as fallback; never the error (secondary) color
const toneColor = (
  colors: ITheme['colors'],
  cultureValues: ICompanyCultureValuePure[] = []
): Record<IllustrationTone, string> => {
  const culture = (index: number, fallback: string) =>
    colors[cultureValues[index]?.color] || fallback
  return {
    surface: colors.lightest,
    line: colors.textLightest,
    muted: colors.textLightest,
    subtle: colors.textLighter,
    accent: colors.primary,
    accentMild: colors.primaryMild,
    accentSoft: colors.primaryFadeLight,
    culture1: culture(0, colors.primaryDark),
    culture2: culture(1, colors.textLight),
    culture3: culture(2, colors.text72),
  }
}

// Draws a schematic illustration in the company's theme colors. Decorative, so
// hidden from assistive technology; renders nothing for an unknown name.
const ReleaseNoteIllustration: React.FC<IProps> = ({ name, className }) => {
  const theme: ITheme = useContext(ThemeContext)
  const { company } = useAppState()
  // Unique per instance; useId's colons are not valid in url(#…) references
  const liftFilterId = `release-note-lift-${useId().replace(/[^\w-]/g, '')}`
  const shapes = releaseNoteIllustrations[name]
  if (!shapes) return null
  const tones = toneColor(theme.colors, company?.cultureValues)

  const paint = (shape: IllustrationShape) => ({
    fill: shape.fill ? tones[shape.fill] : 'none',
    stroke: shape.stroke ? tones[shape.stroke] : undefined,
    strokeWidth: shape.strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    filter: shape.lift ? `url(#${liftFilterId})` : undefined,
  })

  return (
    <Frame className={className} aria-hidden="true">
      <svg
        viewBox="0 0 640 360"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter
            id={liftFilterId}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feDropShadow
              dx="0"
              dy="8"
              stdDeviation="12"
              floodColor={theme.colors.text}
              floodOpacity="0.16"
            />
          </filter>
        </defs>
        {shapes.map((shape, i) => {
          switch (shape.type) {
            case 'rect':
              return (
                <rect
                  key={i}
                  x={shape.x}
                  y={shape.y}
                  width={shape.w}
                  height={shape.h}
                  rx={shape.r}
                  transform={
                    shape.rotate
                      ? `rotate(${shape.rotate} ${shape.x + shape.w / 2} ${
                          shape.y + shape.h / 2
                        })`
                      : undefined
                  }
                  {...paint(shape)}
                />
              )
            case 'circle':
              return (
                <circle
                  key={i}
                  cx={shape.cx}
                  cy={shape.cy}
                  r={shape.r}
                  {...paint(shape)}
                />
              )
            default:
              return <path key={i} d={shape.d} {...paint(shape)} />
          }
        })}
      </svg>
    </Frame>
  )
}

export default ReleaseNoteIllustration

const Frame = styled.div`
  width: 100%;
  height: 260px;
  overflow: hidden;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.primaryLight};

  svg {
    display: block;
  }
`
